import { FALLBACK_TEXT } from '@/utils';
import type { CSSProperties, FC, ReactElement } from 'react';
import { PDF_BORDER, REPORT_BORDER_COLOR } from '../constants';
import { FlexCells } from './FlexCells';
import { ImageCell } from './ImageCell';
import { LinkCell } from './LinkCell';
import { ReportSubsetCells } from './ReportSubsetCells';
import { ReportTd } from './ReportTd';
import { ReportTr } from './ReportTr';
import { TextCell } from './TextCell';
import { Tfoot } from './Tfoot';
import { Thead } from './Thead';

export type TextCellType = {
  type?: 'text';
  value: string | number | null | undefined;
  /**type: textを明示的に指定しないと効かない */
  align?: 'left' | 'center' | 'right';
  textStyle?: CSSProperties;
  textWrapperStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
};
type LinkCellType = {
  type: 'link';
  value: string;
  href: string;
  align?: 'left' | 'center' | 'right';
  textStyle?: CSSProperties;
  textWrapperStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
};
type ElementCell = {
  type: 'element';
  element: ReactElement;
  wrapperStyle?: CSSProperties;
};
type ImageCellType = {
  type: 'image';
  src: string | undefined;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
};
type FlexCell = {
  type?: 'flex';
  flexCells: ReportTableCell[];
  vertical?: boolean;
  cellRatios?: (number | string)[];
  borderWidth?: string;
  borderStyle?: 'dotted' | 'solid';
  wrapperStyle?: CSSProperties;
  isFlexStyle?: boolean;
};
type SubsetCell = {
  type: 'subset';
  containedCell: ReportTableCell;
  includedCell: ReportTableCell;
  cellColRatios?: (number | string)[];
  cellRowRatios?: (number | string)[];
  isContainedCellFullHeight?: boolean;
  wrapperStyle?: CSSProperties;
};

export type ReportTableCell = (
  | TextCellType
  | LinkCellType
  | ElementCell
  | ImageCellType
  | {
      type: 'multiple';
      cell: FlexCell | SubsetCell;
      wrapperStyle?: CSSProperties;
    }
) & { colSpan?: number; rowSpan?: number };

type RatioType = number | `${number}px` | 'auto';

export type ReportTableHeaderCell = (
  | (TextCellType & { ratio: RatioType })
  | (LinkCellType & { ratio: RatioType })
  | (ElementCell & { ratio: RatioType })
  | (ImageCellType & { ratio: RatioType })
  | {
      type: 'multiple';
      cell: FlexCell | SubsetCell;
      ratio: RatioType;
      wrapperStyle?: CSSProperties;
    }
) & { colSpan?: number; rowSpan?: number };

export type ReportTableProps = {
  header: ReportTableHeaderCell[];
  footer?: {
    row: ReportTableCell[];
    rowStyle?: CSSProperties;
  }[];
  data: {
    row: ReportTableCell[];
    rowStyle?: CSSProperties;
  }[];
  tableWidth: string;
  tableStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  defaultBorderStyle?: string;
  borderColor?: 'primary' | 'secondary';
};

export const ReportTable: FC<ReportTableProps> = ({
  header,
  data,
  footer,
  tableWidth,
  tableStyle,
  headerStyle,
  defaultBorderStyle,
  borderColor: borderColorType = 'primary',
}) => {
  const totalRatio = header.reduce((acc, cell) => {
    if (typeof cell.ratio === 'number') {
      return acc + cell.ratio;
    }
    return acc;
  }, 0);
  const calcWidthStyle = (ratio: RatioType) => {
    return typeof ratio === 'number' ? `${(ratio / totalRatio) * 100}%` : ratio;
  };

  const trBorderArgs = defaultBorderStyle
    ? {
        borderLeft: defaultBorderStyle,
        borderRight: defaultBorderStyle,
      }
    : {};

  const borderColor = REPORT_BORDER_COLOR[borderColorType];

  return (
    <table
      style={{
        border: PDF_BORDER.NORMAL,
        borderColor,
        borderCollapse: 'collapse',
        width: tableWidth,
        /*
         * NOTE
         * height: 100% を **<tr> / <td> に付けたまま印刷（react-to-print）に渡すと、Chrome / Edge の印刷エンジンは「1 行＝用紙 1 ページ分」 とみなしてしまいます。
         * 行がページ全体（あるいは 90 vh など）に引き伸ばされる → 実際の文字列は上端に寄り、行と行の間が丸ごと空白 になる──まさに添付のような余白が入るのはバグです。
         * ReportTableでは100%を指定してますが各行で制御を入れるようにしています。
         * https://stackoverflow.com/questions/36690580/how-to-set-div-height-to-100-of-chosen-print-paper
         * https://stackoverflow.com/questions/51360187/css-print-stretch-table-for-the-entire-page-height-100
         */
        height: '100%',
        boxSizing: 'border-box',
        ...tableStyle,
      }}
    >
      <Thead
        style={{
          ...(defaultBorderStyle ? { border: defaultBorderStyle } : {}),
          ...headerStyle,
          borderColor,
        }}
      >
        <ReportTr style={{ border: 'none' }}>
          {header.map((cell, index) => {
            return (
              <ReportTd
                // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                key={index}
                style={{
                  width: calcWidthStyle(cell.ratio),
                  ...trBorderArgs,
                  borderColor,
                  ...cell.wrapperStyle,
                }}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
              >
                {getCell({ cell, borderColor: borderColorType })}
              </ReportTd>
            );
          })}
        </ReportTr>
      </Thead>
      <tbody>
        {data.map((datum, rowIndex) => (
          <ReportTr
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            key={rowIndex}
            style={{
              borderColor,
              ...datum.rowStyle,
            }}
          >
            {datum.row.map((cell, index) => (
              <ReportTd
                // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                key={index}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
                style={{
                  ...trBorderArgs,
                  borderColor,
                  ...cell.wrapperStyle,
                }}
              >
                {getCell({ cell, borderColor: borderColorType })}
              </ReportTd>
            ))}
          </ReportTr>
        ))}
      </tbody>
      {footer && (
        <Tfoot
          style={{
            ...(defaultBorderStyle ? { border: defaultBorderStyle } : {}),
            borderColor,
          }}
        >
          {footer.map((datum, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            <ReportTr key={rowIndex} style={{ ...datum.rowStyle, borderColor }}>
              {datum.row.map((cell, index) => (
                <ReportTd
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  key={index}
                  colSpan={cell.colSpan}
                  rowSpan={cell.rowSpan}
                  style={{
                    ...trBorderArgs,
                    ...cell.wrapperStyle,
                    borderColor,
                  }}
                >
                  {getCell({ cell, borderColor: borderColorType })}
                </ReportTd>
              ))}
            </ReportTr>
          ))}
        </Tfoot>
      )}
    </table>
  );
};

export const getCell = ({
  cell,
  borderColor,
}: {
  cell: ReportTableCell;
  borderColor: 'primary' | 'secondary';
}) => {
  switch (cell.type) {
    case undefined:
    case 'text':
      return (
        <TextCell
          align={cell.align}
          text={cell.value ?? FALLBACK_TEXT}
          textStyle={cell.textStyle}
          textWrapperStyle={cell.textWrapperStyle}
        />
      );
    case 'link':
      return (
        <LinkCell
          text={cell.value}
          align={cell.align}
          href={cell.href}
          textStyle={cell.textStyle}
          textWrapperStyle={cell.textWrapperStyle}
        />
      );

    case 'element':
      return cell.element;
    case 'image':
      return (
        <ImageCell
          src={cell.src}
          style={cell.style}
          wrapperStyle={cell.wrapperStyle}
        />
      );
    case 'multiple':
      return getMultipleCell({ cell: cell.cell, borderColor });
  }
};

const getMultipleCell = ({
  cell,
  borderColor,
}: {
  cell: FlexCell | SubsetCell;
  borderColor: 'primary' | 'secondary';
}) => {
  switch (cell.type) {
    case undefined:
    case 'flex': {
      return (
        <FlexCells
          cells={cell.flexCells}
          vertical={cell.vertical}
          cellRatios={cell.cellRatios}
          borderStyle={cell.borderStyle}
          borderWidth={cell.borderWidth}
          wrapperStyle={cell.wrapperStyle}
          borderColor={borderColor}
          isFlexStyle={cell.isFlexStyle}
        />
      );
    }
    case 'subset':
      return (
        <ReportSubsetCells
          cellColRatios={cell.cellColRatios}
          cellRowRatios={cell.cellRowRatios}
          containedCell={getCell({
            cell: cell.containedCell,
            borderColor,
          })}
          includedCell={getCell({
            cell: cell.includedCell,
            borderColor,
          })}
          isContainedCellFullHeight={cell.isContainedCellFullHeight}
          wrapperStyle={cell.wrapperStyle}
          borderColor={borderColor}
        />
      );
  }
};
